import { Box, CircularProgress, Collapse, useMediaQuery } from '@mui/material';
import { alpha, keyframes, useTheme } from '@mui/material/styles';
import { visuallyHidden } from '@mui/utils';
import {
    useCallback,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useDebounceValue } from 'usehooks-ts';
import { ConnectorNextError } from '~/backend/connector-next-api/connector-next-fetcher';
import { Features } from '~/components/FeatureToggle';
import { useCurrentUserName } from '~/hooks/domain/useCurrentUserName';
import { useIsOpeningInViewer } from '~/hooks/openInViewer';
import { useElementSize } from '~/hooks/useElementSize';
import AppConfig from '~/utils/appconfig';
import { ImportWizard } from './import';
import PatientDrilldown from './PatientDrilldown';
import PatientList from './PatientList';
import { LIST_MIN_WIDTH_COMPACT } from './PatientList/PatientListTable';
import PatientListFilterBar, {
    type FilterMenuState,
} from './PatientListFilterBar';
import PatientListToolbar from './PatientListToolbar';
import { ResizeHandle } from './shared';
import { usePatientList } from './hooks';
import {
    clearFacet,
    DEFAULT_SORT,
    hasActiveMenuFilters,
    hasActivePatientFilters,
    nextSort,
    NO_PATIENT_FILTERS,
    selectedCourseStage,
    toggleFilterValue,
    withAssignedTo,
    withCourseStage,
    type PatientFilterFacetId,
    type SortValue,
    type CourseStage,
} from './model';

const SEARCH_DEBOUNCE_MS = 300;

// Versioned: the panel's default width grew, and a width stored under the
// previous key would hold a returning user at the old, narrower panel.
const PANEL_WIDTH_KEY = 'patient-list-panel-width-v2';
/**
 * The panel carries the patient's whole record — identity, course, and a row
 * per dataset with its facts — so it takes the larger share of the screen,
 * and the list beside it narrows to the patient and their stage.
 */
const DEFAULT_PANEL_WIDTH = 1040;
const MIN_PANEL_WIDTH = 720;
/**
 * Never below the compact table's own minimum, or the list would be laid out
 * wider than the card holding it and the end of the strip would be clipped.
 */
const MIN_LIST_WIDTH = LIST_MIN_WIDTH_COMPACT;
/** Gap between the list card and the panel card; the resize seam lives in it. */
const PANE_GAP = 20;
/** How long the outgoing column set stays over the incoming one while it fades. */
const LAYOUT_CROSSFADE_MS = 200;
const fadeOut = keyframes`
    from { opacity: 1; }
    to { opacity: 0; }
`;

/** A column set and the width it was laid out at, kept while it fades out. */
type ListLayout = { compact: boolean; width: number };

function readStoredPanelWidth(): number {
    try {
        const stored = Number(localStorage.getItem(PANEL_WIDTH_KEY));
        if (Number.isFinite(stored) && stored >= MIN_PANEL_WIDTH) {
            return stored;
        }
    } catch {
        // Local storage can be unavailable; the default is fine.
    }
    return DEFAULT_PANEL_WIDTH;
}

/**
 * The connector-next patient list screen: toolbar, patient list, and the
 * docked per-patient drilldown panel. Owns the query state (search, sort,
 * filters, selection) and hands presentation to the child components.
 */
export default function PatientListScreen() {
    const intl = useIntl();
    const [searchInput, setSearchInput] = useState('');
    const [sort, setSort] = useState(DEFAULT_SORT);
    const [filters, setFilters] = useState(NO_PATIENT_FILTERS);
    // Owned here, not in the filter bar: the toolbar's control opens it too.
    const [filterMenu, setFilterMenu] = useState<FilterMenuState>();
    const [selectedId, setSelectedId] = useState<string>();
    // The patient the panel is built for: the selection while there is one,
    // and the last one while the panel slides shut, so it closes showing its
    // content. Caught up during render rather than in each handler that
    // changes the selection, so none of them can forget it; cleared once the
    // slide has ended.
    const [panelPatientId, setPanelPatientId] = useState<string>();
    if (selectedId !== undefined && selectedId !== panelPatientId) {
        setPanelPatientId(selectedId);
    }
    // True from the moment the panel starts sliding until it has settled.
    const [panelSliding, setPanelSliding] = useState(false);
    const [importOpen, setImportOpen] = useState(false);
    const [panelWidth, setPanelWidth] = useState(readStoredPanelWidth);
    const paneRowRef = useRef<HTMLDivElement>(null);
    const [search] = useDebounceValue(searchInput.trim(), SEARCH_DEBOUNCE_MS);
    // Debounced like search: the menu stays open for multi-select, so raw
    // filters would issue one query per checkbox click.
    const [debouncedFilters] = useDebounceValue(filters, SEARCH_DEBOUNCE_MS);
    const filterMenuId = useId();
    // The stable focus target once a chip or the whole chip row removes itself.
    const filterControlRef = useRef<HTMLButtonElement>(null);
    // Set when the open menu loses its anchor; consumed once the menu is gone,
    // since its focus trap would recapture an immediate focus() call.
    const focusControlAfterMenuClose = useRef(false);
    const openBusy = useIsOpeningInViewer();

    // A PACS retrieval that arrived whole selects the patient it landed in, so the drilldown opens
    // on what was just imported. Set rather than toggled: this is not the user clicking the row,
    // and a toggle would close the panel when the retrieved patient was already the selected one.
    const openRetrievedPatient = useCallback(
        (patientId: string) => setSelectedId(patientId),
        []
    );
    // The element that opened the panel; closing hands focus back to it.
    const restoreFocusRef = useRef<HTMLElement>();

    const {
        patients,
        totalCount,
        hasMore,
        loadMore,
        isLoadingMore,
        isLoading,
        isRefreshing,
        isError,
        error,
    } = usePatientList({
        search: search || undefined,
        sort,
        filters: debouncedFilters,
    });

    const handleSelect = useCallback((id: string) => {
        restoreFocusRef.current =
            document.activeElement instanceof HTMLElement
                ? document.activeElement
                : undefined;
        setSelectedId((current) => (current === id ? undefined : id));
    }, []);

    // The synchronous focus() runs while the panel is still mounted, so focus
    // never lands on <body>; a row unmounted by a refetch is left alone.
    const closePanel = useCallback(() => {
        setSelectedId(undefined);
        const target = restoreFocusRef.current;
        if (target?.isConnected) {
            target.focus();
        }
    }, []);

    const handleSort = useCallback((key: SortValue) => {
        setSort((current) => nextSort(current, key));
    }, []);

    const focusFilterControl = useCallback(() => {
        filterControlRef.current?.focus();
    }, []);

    const handleFilterToggle = useCallback(
        (facetId: PatientFilterFacetId, value: string) => {
            const next = toggleFilterValue(filters, facetId, value);
            setFilters(next);
            // A chip-anchored menu loses its anchor when the toggle empties
            // the chip's facet, so close it rather than leave it on a
            // detached node.
            if (
                filterMenu?.origin === 'chip' &&
                next[filterMenu.facetId].length === 0
            ) {
                focusControlAfterMenuClose.current = true;
                setFilterMenu(undefined);
            }
        },
        [filters, filterMenu]
    );

    useEffect(() => {
        if (filterMenu === undefined && focusControlAfterMenuClose.current) {
            focusControlAfterMenuClose.current = false;
            focusFilterControl();
        }
    }, [filterMenu, focusFilterControl]);

    // The synchronous focus() runs before the activated control unmounts with
    // its chip (or the whole chip row), so focus never lands on <body>.
    const handleFacetClear = useCallback(
        (facetId: PatientFilterFacetId) => {
            focusFilterControl();
            setFilters((current) => clearFacet(current, facetId));
        },
        [focusFilterControl]
    );

    const clearFilters = useCallback(() => {
        focusFilterControl();
        setFilters(NO_PATIENT_FILTERS);
    }, [focusFilterControl]);

    const handleCourseStageChange = useCallback(
        (stage: CourseStage | undefined) => {
            setFilters((current) => withCourseStage(current, stage));
        },
        []
    );

    // "Assigned to me" is offered only where patients can be assigned: over a
    // cohort nothing assigns, its one possible result is an empty list, which
    // would read as "no patients are assigned to you". It also needs a "me":
    // without a signed-in name the toggle is offered disabled rather than
    // matching nobody.
    const assignmentEnabled = AppConfig.isFeatureEnabled(
        Features.PATIENT_ASSIGNMENT
    );
    const currentUserName = useCurrentUserName();
    const handleAssignedToMeChange = useMemo(
        () =>
            currentUserName === undefined
                ? undefined
                : (assignedToMe: boolean) =>
                      setFilters((current) =>
                          withAssignedTo(
                              current,
                              assignedToMe ? currentUserName : undefined
                          )
                      ),
        [currentUserName]
    );

    const clearSearchAndFilters = useCallback(() => {
        focusFilterControl();
        setFilters(NO_PATIENT_FILTERS);
        setSearchInput('');
    }, [focusFilterControl]);

    const openFilterMenu = useCallback(
        (anchor: HTMLElement) => setFilterMenu({ origin: 'toolbar', anchor }),
        []
    );

    const filterMenuOpen = filterMenu !== undefined;
    // The control opens the menu, so it is tinted for the menu's own
    // dimensions; the strip shows its selection itself.
    const filtersActive = hasActiveMenuFilters(filters);
    // Search narrows the same query as the filters, so every "this list is
    // not the cohort" surface keys on both. Derived from the debounced values
    // the query uses, so it describes the presented result.
    const isNarrowed =
        search !== '' || hasActivePatientFilters(debouncedFilters);

    // A changed search or filter re-scopes the list; a selection made under
    // the old scope must not present a patient the new one excludes.
    useEffect(() => {
        setSelectedId(undefined);
    }, [search, debouncedFilters]);

    const openImport = useCallback(() => setImportOpen(true), []);

    const closeImport = useCallback(() => setImportOpen(false), []);

    const panelOpen = selectedId !== undefined;

    // Escape closes the patient panel, but never while the import dialog is
    // open (it owns its own Escape) nor while an open is in flight. The open's
    // dialogs are the ones that own Escape then, and the incomplete-retrieval
    // dialog deliberately does not consume the key, so a listener here would
    // close the panel out from underneath an unanswered clinical question and
    // take the selection the answer applies to with it.
    useEffect(() => {
        if (!panelOpen || importOpen || openBusy) {
            return;
        }
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                closePanel();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [panelOpen, importOpen, openBusy, closePanel]);

    // Tracks the space the two panes share, so the panel can never be dragged — or restored from
    // a wider session — past the point where the list still has room to be read.
    const paneRowWidth = useElementSize(paneRowRef)?.widthPx ?? 0;

    const maxPanelWidth = Math.max(
        MIN_PANEL_WIDTH,
        paneRowWidth - PANE_GAP - MIN_LIST_WIDTH
    );
    // Clamped for display only: a narrow window never overwrites the stored preference.
    const effectivePanelWidth = Math.min(panelWidth, maxPanelWidth);
    // The list takes the column set of the panel's target state at once and,
    // while the panel slides, is laid out at the width it will have when the
    // slide ends, its card clipping it: the columns and timelines never
    // reflow on a frame of the slide. At rest the flex layout gives the same
    // width, so nothing moves when the slide ends.
    const compact = panelOpen;
    const listWidthWithPanel = Math.max(
        0,
        paneRowWidth - PANE_GAP - effectivePanelWidth
    );
    const listWidth =
        panelSliding && paneRowWidth > 0
            ? compact
                ? listWidthWithPanel
                : paneRowWidth
            : undefined;
    // A column-set switch cannot be animated, so it is crossfaded: the
    // outgoing set stays painted over the incoming one, at the width it had,
    // and fades out. It needs the row's measurement to lay that width out,
    // and is skipped under reduced motion.
    const { transitions } = useTheme();
    const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
    const [shownCompact, setShownCompact] = useState(compact);
    const [outgoingLayout, setOutgoingLayout] = useState<ListLayout>();
    if (compact !== shownCompact) {
        setShownCompact(compact);
        if (paneRowWidth > 0 && !reducedMotion) {
            setOutgoingLayout({
                compact: shownCompact,
                width: shownCompact ? listWidthWithPanel : paneRowWidth,
            });
        }
    }
    useEffect(() => {
        if (outgoingLayout === undefined) {
            return;
        }
        const timer = setTimeout(
            () => setOutgoingLayout(undefined),
            LAYOUT_CROSSFADE_MS
        );
        return () => clearTimeout(timer);
    }, [outgoingLayout]);
    const listScrollRef = useRef<HTMLDivElement>(null);

    const persistPanelWidth = useCallback((width: number) => {
        const rounded = Math.round(width);
        setPanelWidth(rounded);
        try {
            localStorage.setItem(PANEL_WIDTH_KEY, String(rounded));
        } catch {
            // Local storage can be unavailable; the width still applies for this session.
        }
    }, []);

    const errorMessage = useMemo(() => {
        if (!isError) {
            return undefined;
        }
        // ConnectorNextError.message is the server's fixed `title` (PHI-safe);
        // anything else gets generic copy.
        return error instanceof ConnectorNextError
            ? error.message
            : intl.formatMessage({ id: 'patientList.error.load' });
    }, [isError, error, intl]);

    // Everything the list shows apart from its column set, which the live
    // list and its fading copy take separately.
    const listProps = {
        patients,
        totalCount,
        hasMore,
        onLoadMore: loadMore,
        isLoadingMore,
        isLoading,
        errorMessage,
        selectedId,
        onSelect: handleSelect,
        sort,
        onSortChange: handleSort,
        isNarrowed,
        onClearSearchAndFilters: clearSearchAndFilters,
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                bgcolor: 'background.sunken',
                overflow: 'hidden',
            }}
        >
            <PatientListToolbar
                totalCount={isLoading || isError ? undefined : totalCount}
                isNarrowed={isNarrowed}
                searchInput={searchInput}
                onSearchInputChange={setSearchInput}
                filtersActive={filtersActive}
                onOpenFilterMenu={openFilterMenu}
                filterMenuExpanded={filterMenu?.origin === 'toolbar'}
                filterMenuId={filterMenuId}
                filterButtonRef={filterControlRef}
                courseStage={selectedCourseStage(filters)}
                onCourseStageChange={handleCourseStageChange}
                assignedToMe={
                    assignmentEnabled
                        ? {
                              pressed: filters.assignedTo !== undefined,
                              onChange: handleAssignedToMeChange,
                          }
                        : undefined
                }
                isFetching={isRefreshing}
                onImport={openImport}
                activeFilters={
                    <PatientListFilterBar
                        filters={filters}
                        onToggle={handleFilterToggle}
                        onClearFacet={handleFacetClear}
                        onClear={clearFilters}
                        menu={filterMenu}
                        onMenuChange={setFilterMenu}
                        menuId={filterMenuId}
                    />
                }
            />
            <ImportWizard
                open={importOpen}
                onClose={closeImport}
                onRetrieved={openRetrievedPatient}
            />
            {/* Screen-reader-only echo of the narrowed count: chips and footer
                convey it visually only. Announced only once the query has
                settled (a placeholder total would misstate the match count)
                and the menu is closed, since the open menu is a modal that
                removes this subtree from the accessibility tree. */}
            <Box role="status" aria-live="polite" sx={visuallyHidden}>
                {isNarrowed &&
                !isLoading &&
                !isRefreshing &&
                !isError &&
                !filterMenuOpen
                    ? intl.formatMessage(
                          { id: 'patientList.filter.matchCount' },
                          { totalCount }
                      )
                    : ''}
            </Box>
            <Box
                ref={paneRowRef}
                sx={{
                    flex: 1,
                    display: 'flex',
                    minHeight: 0,
                    px: 3.5,
                    pt: 2.5,
                    pb: 3,
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative',
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                        bgcolor: 'background.default',
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        ref={listScrollRef}
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            overflow: 'auto',
                            width: listWidth,
                        }}
                    >
                        <PatientList {...listProps} compact={compact} />
                    </Box>
                    {outgoingLayout && (
                        <Box
                            aria-hidden
                            // Scrolled as far as the list it covers, so the
                            // same rows fade into each other.
                            ref={(overlay: HTMLDivElement | null) => {
                                if (overlay) {
                                    overlay.scrollTop =
                                        listScrollRef.current?.scrollTop ?? 0;
                                }
                            }}
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                bottom: 0,
                                width: outgoingLayout.width,
                                overflow: 'hidden',
                                bgcolor: 'background.default',
                                pointerEvents: 'none',
                                animation: `${fadeOut} ${LAYOUT_CROSSFADE_MS}ms ease-out forwards`,
                            }}
                        >
                            {/* Never loads: its sentinel would request
                                the same page as the live list's. */}
                            <PatientList
                                {...listProps}
                                compact={outgoingLayout.compact}
                                hasMore={false}
                            />
                        </Box>
                    )}
                    {isRefreshing && (
                        <Box
                            aria-hidden
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'center',
                                pt: 6,
                                bgcolor: (theme) =>
                                    alpha(
                                        theme.palette.background.default,
                                        0.4
                                    ),
                                pointerEvents: 'none',
                            }}
                        >
                            <CircularProgress size={22} />
                        </Box>
                    )}
                </Box>

                {/* The panel slides in from the right and back out, the list
                    card narrowing and widening with it instead of snapping:
                    the slot sits at the row's right edge, so its left edge is
                    what moves and the panel moves with it. Once open the
                    slot's width follows its content, so dragging the seam
                    still resizes the panel. */}
                <Collapse
                    in={panelOpen}
                    orientation="horizontal"
                    unmountOnExit
                    timeout={
                        reducedMotion
                            ? 0
                            : {
                                  enter: transitions.duration.enteringScreen,
                                  exit: transitions.duration.leavingScreen,
                              }
                    }
                    onEnter={() => setPanelSliding(true)}
                    onEntered={() => setPanelSliding(false)}
                    onExit={() => setPanelSliding(true)}
                    onExited={() => {
                        setPanelSliding(false);
                        setPanelPatientId(undefined);
                    }}
                    sx={{
                        flexShrink: 0,
                        '& .MuiCollapse-wrapperInner': { display: 'flex' },
                    }}
                >
                    {panelPatientId !== undefined && (
                        <>
                            <ResizeHandle
                                value={effectivePanelWidth}
                                min={MIN_PANEL_WIDTH}
                                max={maxPanelWidth}
                                onChange={setPanelWidth}
                                onCommit={persistPanelWidth}
                                pane="after"
                                width={PANE_GAP}
                            />
                            {/* key remounts the panel per patient so tab/selection
                                state can never carry (even for one frame) across
                                patients. */}
                            <PatientDrilldown
                                key={panelPatientId}
                                patientId={panelPatientId}
                                width={effectivePanelWidth}
                                onClose={closePanel}
                            />
                        </>
                    )}
                </Collapse>
            </Box>
        </Box>
    );
}
